import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getTableData,
  exportTableCsv,
  updateColumnClassification,
  requestColumnIdentification,
  getColumnIdentifyStatus,
  requestDpvAnnotation,
  getDpvStatus,
  getLlmSettings,
  updateLlmSettings,
  getReconciliationSettings,
  createReconciliationJob,
  getReconciliationStatus,
  triggerReconciliationColumnTypes,
  getReconciliationColumnTypes,
  getReconciliationCandidates,
  updateReconciliationCell
} from '../services/apiServices';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Button,
  Divider,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  TextField,
  Checkbox,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BuildIcon from '@mui/icons-material/Build';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PolicyIcon from '@mui/icons-material/Policy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TableHeader from './TableHeader';
import TableSearch from './TableSearch';
import TableSortControls from './TableSortControls';
import TypeFilterModal from './TypeFilterModal';
import { COLUMN_TYPES, LIT_TYPES, NER_TYPES } from '../constants/columnTypes';

const TruncatedCell = ({ content, maxLength = 100 }) => {
  if (!content) return null;
  const text = String(content);
  if (text.length <= maxLength) {
    return <span>{text}</span>;
  }
  return <span>{text.substring(0, maxLength)}...</span>;
};

const LIT_COARSE_MAP = {
  'xsd:integer': 'NUMBER',
  'xsd:decimal': 'NUMBER',
  'xsd:boolean': 'STRING',
  'xsd:string': 'STRING',
  'xsd:date': 'DATETIME',
  'xsd:datetime': 'DATETIME',
  'xsd:anyuri': 'STRING',
  'ext:email': 'STRING',
  'ext:ipv4': 'STRING',
  'ext:ipv6': 'STRING',
  'ext:phone': 'STRING',
  'ext:uuid': 'STRING',
  'ext:url': 'STRING',
  'ext:postalcode': 'STRING',
  'ext:countrycode': 'STRING',
  'ext:currencycode': 'STRING',
  'ext:lat': 'NUMBER',
  'ext:lon': 'NUMBER',
  'ext:percentage': 'NUMBER',
  'ext:duration': 'NUMBER'
};

const getLitCoarseType = (value) => {
  const raw = value ? String(value).trim() : '';
  if (!raw) return '';
  const upper = raw.toUpperCase();
  if (LIT_TYPES.includes(upper)) {
    return upper;
  }
  if (upper.startsWith('LIT:')) {
    const litValue = upper.slice(4);
    if (LIT_TYPES.includes(litValue)) {
      return litValue;
    }
  }
  const mapped = LIT_COARSE_MAP[raw.toLowerCase()];
  if (mapped) return mapped;
  const lower = raw.toLowerCase();
  if (lower.includes('date') || lower.includes('time')) {
    return 'DATETIME';
  }
  if (
    lower.includes('int') ||
    lower.includes('decimal') ||
    lower.includes('number') ||
    lower.includes('percent') ||
    lower.includes('duration') ||
    lower.includes('lat') ||
    lower.includes('lon')
  ) {
    return 'NUMBER';
  }
  return 'STRING';
};

const getSpecificSubtype = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value.type_id || value.specific || value.type || value.subtype || value.value || '';
  }
  return String(value);
};

const getFineSubtype = (value) => {
  if (!value || typeof value !== 'object') return '';
  return value.fine_type_id || value.fine || value.fineTypeId || value.fineType || '';
};

const parseOptionalNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const sameNumberArray = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (Number(left[i]) !== Number(right[i])) return false;
  }
  return true;
};

const getClassificationTypeDetails = (value, group) => {
  if (!value) return null;

  const isObject = typeof value === 'object';
  const typeId = isObject
    ? (value.type_id || value.typeId || value.type || value.specific || '')
    : String(value);
  const coarseTypeId = isObject
    ? (value.coarse_type_id || value.coarseTypeId || value.coarse || value.coarse_type || '')
    : '';
  const fineTypeId = isObject ? getFineSubtype(value) : '';
  const confidence = isObject ? parseOptionalNumber(value.confidence ?? value.score) : null;
  const fineConfidence = isObject
    ? parseOptionalNumber(value.fine_confidence ?? value.fineConfidence ?? value.fine_score)
    : null;

  if (!(typeId || coarseTypeId || fineTypeId || confidence !== null || fineConfidence !== null)) {
    return null;
  }

  return {
    group,
    typeId: typeId || '',
    coarseTypeId: coarseTypeId || '',
    fineTypeId: fineTypeId || '',
    confidence,
    fineConfidence
  };
};

const getCoarseSubtype = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    const coarse = value.coarse_type_id || value.coarse || value.coarse_type || '';
    return getLitCoarseType(coarse || value.type_id || value.specific || '');
  }
  return getLitCoarseType(value);
};

const getCoarseNeSubtype = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return value.coarse_type_id || value.coarse || value.coarse_type || value.type_id || value.specific || value.type || '';
  }
  return String(value);
};

const buildClassificationState = (headers, classifiedColumns) => {
  const state = {};
  headers.forEach((_, idx) => {
    if (classifiedColumns?.NE?.hasOwnProperty(idx)) {
      const rawSubtype = getSpecificSubtype(classifiedColumns.NE[idx]);
      state[idx] = {
        type: 'NE',
        subtype: rawSubtype,
        rawSubtype,
        derivedSubtype: rawSubtype
      };
    } else if (classifiedColumns?.LIT?.hasOwnProperty(idx)) {
      const rawSubtype = getSpecificSubtype(classifiedColumns.LIT[idx]);
      const derivedSubtype = getCoarseSubtype(classifiedColumns.LIT[idx]);
      state[idx] = {
        type: 'LIT',
        subtype: derivedSubtype,
        rawSubtype,
        derivedSubtype
      };
    } else {
      state[idx] = {
        type: 'IGNORED',
        subtype: '',
        rawSubtype: '',
        derivedSubtype: ''
      };
    }
  });
  return state;
};

const buildColumnClassificationPayload = (classificationState = {}) => {
  const NE = {};
  const LIT = {};
  Object.entries(classificationState).forEach(([idx, entry]) => {
    const { type, subtype, rawSubtype, derivedSubtype } = entry || {};
    if (type === 'NE' && subtype) {
      NE[idx] = subtype;
    } else if (type === 'LIT' && subtype) {
      const keepRaw = rawSubtype && derivedSubtype && subtype === derivedSubtype;
      LIT[idx] = keepRaw ? rawSubtype : subtype;
    }
  });
  const payload = {};
  if (Object.keys(NE).length) payload.NE = NE;
  if (Object.keys(LIT).length) payload.LIT = LIT;
  return payload;
};

const EXPORT_ENRICHMENT_OPTIONS = [
  { key: 'id', label: 'Entity ID' },
  { key: 'name', label: 'Entity name' },
  { key: 'description', label: 'Description' },
  { key: 'types', label: 'Types' },
  { key: 'score', label: 'Score' },
  { key: 'match', label: 'Match flag' }
];

const DEFAULT_SORT_PARAMS = {
  sortBy: 'id',
  sortDirection: 'asc',
  sortConfidenceColumn: null
};

const DEFAULT_RECON_PROVIDER = 'lion_linker';

const PROVIDER_LABEL_OVERRIDES = {
  lion_linker: 'Lion Linker',
  crocodile: 'Crocodile',
  refined: 'ReFinED',
  wikidata: 'Wikidata Reconciler'
};

const getProviderLabel = (providerId, providerMeta = null) => {
  if (providerMeta?.label) return providerMeta.label;
  if (PROVIDER_LABEL_OVERRIDES[providerId]) return PROVIDER_LABEL_OVERRIDES[providerId];
  if (!providerId) return 'Reconciler';
  return providerId
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getProviderTag = (providerId, providerMeta = null) => {
  const label = getProviderLabel(providerId, providerMeta);
  const parts = label.replace(/[^A-Za-z0-9 ]+/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 3).toUpperCase();
  }
  return 'REC';
};

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatReconcileProgress = (progress) => {
  if (!progress || typeof progress !== 'object') return '';

  const totalCells = toFiniteNumber(progress.total_cells);
  const processedCells = toFiniteNumber(progress.processed_cells);
  const totalMentions = toFiniteNumber(progress.total_mentions);
  const processedMentions = toFiniteNumber(progress.processed_mentions);
  const percent = toFiniteNumber(progress.percent);
  const clampedPercent = percent === null
    ? null
    : Math.max(0, Math.min(100, percent));
  const percentLabel = clampedPercent === null
    ? ''
    : `${Math.round(clampedPercent)}%`;

  if (totalCells !== null && totalCells > 0 && processedCells !== null) {
    const done = Math.max(0, Math.min(Math.round(totalCells), Math.round(processedCells)));
    const total = Math.round(totalCells);
    return `${done}/${total} cells${percentLabel ? ` (${percentLabel})` : ''}`;
  }
  if (totalMentions !== null && totalMentions > 0 && processedMentions !== null) {
    const done = Math.max(0, Math.min(Math.round(totalMentions), Math.round(processedMentions)));
    const total = Math.round(totalMentions);
    return `${done}/${total} mentions${percentLabel ? ` (${percentLabel})` : ''}`;
  }
  if (percentLabel) return percentLabel;
  return '';
};

const formatReconcileRuntimeStatus = (statusValue, progress = null, providerId = null) => {
  const normalized = String(statusValue || 'queued').toLowerCase();
  const supportsClientProgress = String(providerId || '').toLowerCase() === 'wikidata';
  if (!supportsClientProgress) {
    return `Reconciliation ${normalized}...`;
  }
  const phase = String(progress?.phase || '').toLowerCase();
  const progressLabel = formatReconcileProgress(progress);

  let label = normalized;
  if (phase === 'querying') {
    label = 'querying Wikidata';
  } else if (phase === 'applying') {
    label = 'applying matches';
  } else if (phase === 'completed') {
    label = 'completed';
  }

  return progressLabel
    ? `Reconciliation ${label}... ${progressLabel}`
    : `Reconciliation ${label}...`;
};

const TableDataViewer = () => {
  const navigate = useNavigate();
  const { datasetName, tableName } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);
  const [reconcilePanelExpanded, setReconcilePanelExpanded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    includeTypes: [],
    excludeTypes: [],
    includeNeTypes: [],
    excludeNeTypes: []
  });
  const [sortParams, setSortParams] = useState(DEFAULT_SORT_PARAMS);
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);
  const [columnEditorOpen, setColumnEditorOpen] = useState(false);
  const [columnClassification, setColumnClassification] = useState({});
  const [columnEditorError, setColumnEditorError] = useState(null);
  const [autoDetectStatus, setAutoDetectStatus] = useState(null);
  const [autoIdentifyOpen, setAutoIdentifyOpen] = useState(false);
  const [autoIdentifySubmitting, setAutoIdentifySubmitting] = useState(false);
  const [autoIdentifyPolling, setAutoIdentifyPolling] = useState(false);
  const [dpvDetectStatus, setDpvDetectStatus] = useState(null);
  const [dpvIdentifyOpen, setDpvIdentifyOpen] = useState(false);
  const [dpvIdentifySubmitting, setDpvIdentifySubmitting] = useState(false);
  const [dpvIdentifyPolling, setDpvIdentifyPolling] = useState(false);
  const [autoIdentifyConfig, setAutoIdentifyConfig] = useState({
    provider: '',
    model: ''
  });
  const [llmApiKey, setLlmApiKey] = useState('');
  const [llmHasApiKey, setLlmHasApiKey] = useState(false);
  const [showLlmApiKeyInput, setShowLlmApiKeyInput] = useState(false);
  const [llmOptions, setLlmOptions] = useState({
    providers: [],
    endpoints: []
  });
  const [llmSettingsError, setLlmSettingsError] = useState(null);
  const [showDpvAnnotations, setShowDpvAnnotations] = useState(false);
  const [compactTable, setCompactTable] = useState(false);
  const [reconcileScope, setReconcileScope] = useState('cell');
  const [reconcileColumns, setReconcileColumns] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [reconcileTopK, setReconcileTopK] = useState(5);
  const [reconcileJobId, setReconcileJobId] = useState(null);
  const [reconcileStatus, setReconcileStatus] = useState(null);
  const [reconcileSubmitting, setReconcileSubmitting] = useState(false);
  const [reconcilePolling, setReconcilePolling] = useState(false);
  const [reconcileProvider, setReconcileProvider] = useState(DEFAULT_RECON_PROVIDER);
  const [reconcileSettings, setReconcileSettings] = useState({
    availableProviders: [DEFAULT_RECON_PROVIDER],
    llm: {
      isConfigured: false,
      missing: []
    },
    providers: {
      [DEFAULT_RECON_PROVIDER]: {
        id: DEFAULT_RECON_PROVIDER,
        label: getProviderLabel(DEFAULT_RECON_PROVIDER),
        hasApiKey: false,
        requiresApiKey: true,
        usesSharedLlm: true,
        missing: ['Lion Linker API key']
      }
    }
  });
  const [reconcileColumnTypes, setReconcileColumnTypes] = useState(null);
  const [reconcileColumnTypesStatus, setReconcileColumnTypesStatus] = useState('UNSET');
  const [reconcileColumnTypesJobId, setReconcileColumnTypesJobId] = useState(null);
  const [reconcileColumnTypesConfig, setReconcileColumnTypesConfig] = useState(null);
  const [reconcileColumnTypesTriggering, setReconcileColumnTypesTriggering] = useState(false);
  const [reconcileColumnTypesError, setReconcileColumnTypesError] = useState(null);
  const [reconcileTypeSampleStrategy, setReconcileTypeSampleStrategy] = useState('auto');
  const [reconcileTypeSampleSize, setReconcileTypeSampleSize] = useState(5000);
  const [selectedNeColumn, setSelectedNeColumn] = useState(null);
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateError, setCandidateError] = useState(null);
  const [candidatePayload, setCandidatePayload] = useState(null);
  const [candidateCellMeta, setCandidateCellMeta] = useState(null);
  const [candidateSelection, setCandidateSelection] = useState(null);
  const [candidateSaving, setCandidateSaving] = useState(false);
  const [candidateSaveError, setCandidateSaveError] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportIncludeReconciliation, setExportIncludeReconciliation] = useState(true);
  const [exportEnrichmentFields, setExportEnrichmentFields] = useState([
    'id',
    'name',
    'description',
    'types',
    'score'
  ]);
  const [exportSubmitting, setExportSubmitting] = useState(false);
  const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false);
  const [autoIdentifyOverwriteConfirmOpen, setAutoIdentifyOverwriteConfirmOpen] = useState(false);
  const [columnTypeDetailsOpen, setColumnTypeDetailsOpen] = useState(false);
  const [selectedTypeDetailsIndex, setSelectedTypeDetailsIndex] = useState(null);

  const fetchTableData = useCallback(async (options = {}) => {
    setLoading(true);
    try {
      const response = await getTableData(datasetName, tableName, 10, {
        ...options,
        search: searchText || undefined,
        includeTypes: activeFilters.includeTypes?.length > 0 ? activeFilters.includeTypes : undefined,
        excludeTypes: activeFilters.excludeTypes?.length > 0 ? activeFilters.excludeTypes : undefined,
        includeNeTypes: activeFilters.includeNeTypes?.length > 0 ? activeFilters.includeNeTypes : undefined,
        excludeNeTypes: activeFilters.excludeNeTypes?.length > 0 ? activeFilters.excludeNeTypes : undefined,
        sortBy: sortParams.sortBy || undefined,
        sortDirection: sortParams.sortDirection || undefined,
        sortConfidenceColumn: sortParams.sortConfidenceColumn !== null ? sortParams.sortConfidenceColumn : undefined
      });
      if (response.data) {
        setData(response.data);
        setNextCursor(response.pagination?.next_cursor || null);
        setPrevCursor(response.pagination?.prev_cursor || null);
        setError(null);
      } else {
        setError('No data available');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  }, [datasetName, tableName, searchText, activeFilters, sortParams]);

  const fetchReconciliationColumnTypes = useCallback(async () => {
    try {
      const response = await getReconciliationColumnTypes(datasetName, tableName);
      setReconcileColumnTypes(response?.result || null);
      setReconcileColumnTypesStatus(response?.status || 'UNSET');
      setReconcileColumnTypesJobId(response?.job_id || null);
      setReconcileColumnTypesConfig(response?.config || null);
      if (response?.error?.detail || response?.error) {
        setReconcileColumnTypesError(response?.error?.detail || response?.error);
      } else {
        setReconcileColumnTypesError(null);
      }
    } catch (err) {
      setReconcileColumnTypesError(
        err?.response?.data?.detail || err?.message || 'Unable to load NE column type ranking.'
      );
      setReconcileColumnTypesStatus('FAILED');
    }
  }, [datasetName, tableName]);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  useEffect(() => {
    fetchReconciliationColumnTypes();
  }, [fetchReconciliationColumnTypes]);

  useEffect(() => {
    if (data?.header) {
      setColumnClassification(buildClassificationState(data.header, data.classified_columns));
    }
  }, [data]);

  useEffect(() => {
    const header = data?.header || [];
    const classified = data?.classified_columns || {};
    const neMap = classified?.NE || {};
    const neIndices = Object.keys(neMap)
      .map((key) => Number(key))
      .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < header.length)
      .sort((left, right) => left - right);

    setReconcileColumns((prev) => {
      if (neIndices.length === 0) {
        return [];
      }
      const previous = Array.isArray(prev)
        ? prev
          .map((idx) => Number(idx))
          .filter((idx) => Number.isInteger(idx) && neIndices.includes(idx))
          .sort((left, right) => left - right)
        : [];
      const next = previous.length > 0 ? previous : neIndices;
      return sameNumberArray(previous, next) ? prev : next;
    });
  }, [data?.header, data?.classified_columns]);

  useEffect(() => {
    setSelectedRows(new Set());
    setSelectedCells(new Set());
  }, [data?.rows, reconcileScope]);

  useEffect(() => {
    const header = data?.header || [];
    const neColumns = data?.classified_columns?.NE || {};
    const neSet = new Set(
      Object.keys(neColumns)
        .map((key) => Number(key))
        .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < header.length)
    );
    setSelectedCells((prev) => {
      const next = new Set(
        Array.from(prev).filter((entry) => {
          const parts = String(entry).split(':');
          if (parts.length !== 2) return false;
          const colIdx = Number(parts[1]);
          return Number.isInteger(colIdx) && neSet.has(colIdx);
        })
      );
      return next.size === prev.size ? prev : next;
    });
  }, [data?.header, data?.classified_columns]);

  useEffect(() => {
    if (data?.classification_status === 'AUTO_PENDING') {
      if (!autoIdentifyPolling) {
        setAutoIdentifyPolling(true);
      }
      if (!autoDetectStatus) {
        setAutoDetectStatus('Auto-identification in progress...');
      }
    }
  }, [data?.classification_status, autoIdentifyPolling, autoDetectStatus]);

  useEffect(() => {
    let isMounted = true;
    const loadSettings = async () => {
      try {
        const [settings, reconSettings] = await Promise.all([
          getLlmSettings(),
          getReconciliationSettings()
        ]);
        if (!isMounted) return;
        const providers = settings?.allowed_providers || [];
        const configuredProvider = settings?.provider || '';
        const providerValid = !configuredProvider || providers.length === 0 || providers.includes(configuredProvider);
        setAutoIdentifyConfig({
          provider: providerValid ? configuredProvider : '',
          model: settings?.model || ''
        });
        const hasKey = Boolean(settings?.has_api_key);
        setLlmHasApiKey(hasKey);
        setShowLlmApiKeyInput(!hasKey);
        setLlmOptions({
          providers,
          endpoints: settings?.allowed_endpoints || []
        });
        setLlmSettingsError(
          providerValid ? null : 'Saved LLM provider is not supported by this server.'
        );
        const availableProviders = Array.isArray(reconSettings?.available_providers) &&
          reconSettings.available_providers.length > 0
          ? reconSettings.available_providers
          : [DEFAULT_RECON_PROVIDER];
        const sharedLlm = reconSettings?.llm || {};
        const llmConfigured = typeof sharedLlm?.is_configured === 'boolean'
          ? sharedLlm.is_configured
          : Boolean(settings?.is_configured);
        const rawProviderMap = reconSettings?.reconciler_providers || reconSettings?.reconcilers || {};
        const normalizedProviders = {};
        availableProviders.forEach((providerId) => {
          const providerPayload = rawProviderMap?.[providerId] || reconSettings?.[providerId] || {};
          const hasApiKey = Boolean(
            providerPayload?.has_api_key ||
            (providerId === 'lion_linker' ? reconSettings?.has_api_key : false) ||
            (providerId === 'crocodile' ? reconSettings?.crocodile_has_api_key : false) ||
            (providerId === 'refined' ? reconSettings?.refined_has_api_key : false) ||
            (providerId === 'wikidata' ? reconSettings?.wikidata_has_api_key : false)
          );
          const hasLamapiToken = Boolean(
            providerPayload?.has_lamapi_token ||
            (providerId === 'lion_linker' ? reconSettings?.has_lamapi_token : false)
          );
          const requiresApiKey = providerPayload?.requires_api_key !== undefined
            ? Boolean(providerPayload.requires_api_key)
            : providerId !== 'wikidata';
          let missing = Array.isArray(providerPayload?.missing)
            ? providerPayload.missing.filter(Boolean)
            : [];
          if (missing.length === 0 && providerId === 'lion_linker') {
            missing = [];
            if (!hasApiKey) missing.push('Lion Linker API key');
            if (!hasLamapiToken) missing.push('Lamapi token');
            if (!llmConfigured) missing.push('LLM provider/model/API key');
          }
          normalizedProviders[providerId] = {
            id: providerId,
            label: getProviderLabel(providerId, providerPayload),
            hasApiKey,
            hasLamapiToken,
            requiresApiKey,
            usesSharedLlm: Boolean(providerPayload?.uses_shared_llm || providerId === 'lion_linker'),
            missing
          };
        });
        if (!normalizedProviders[DEFAULT_RECON_PROVIDER]) {
          normalizedProviders[DEFAULT_RECON_PROVIDER] = {
            id: DEFAULT_RECON_PROVIDER,
            label: getProviderLabel(DEFAULT_RECON_PROVIDER),
            hasApiKey: false,
            hasLamapiToken: false,
            requiresApiKey: true,
            usesSharedLlm: true,
            missing: ['Lion Linker API key']
          };
        }
        setReconcileSettings({
          availableProviders,
          llm: {
            isConfigured: llmConfigured,
            missing: Array.isArray(sharedLlm?.missing) ? sharedLlm.missing : []
          },
          providers: normalizedProviders
        });
        const defaultProvider = availableProviders.includes(reconSettings?.provider)
          ? reconSettings.provider
          : (availableProviders[0] || DEFAULT_RECON_PROVIDER);
        setReconcileProvider(defaultProvider);
      } catch (err) {
        const storedProvider = localStorage.getItem('koala.llmProvider') || '';
        const storedModel = localStorage.getItem('koala.llmModel') || '';
        if (!isMounted) return;
        setAutoIdentifyConfig({
          provider: storedProvider,
          model: storedModel
        });
        setLlmHasApiKey(false);
        setShowLlmApiKeyInput(true);
        setLlmSettingsError('Unable to load LLM settings from the profile.');
        setReconcileSettings({
          availableProviders: [DEFAULT_RECON_PROVIDER],
          llm: {
            isConfigured: false,
            missing: []
          },
          providers: {
            [DEFAULT_RECON_PROVIDER]: {
              id: DEFAULT_RECON_PROVIDER,
              label: getProviderLabel(DEFAULT_RECON_PROVIDER),
              hasApiKey: false,
              hasLamapiToken: false,
              requiresApiKey: true,
              usesSharedLlm: true,
              missing: ['Lion Linker API key']
            }
          }
        });
        setReconcileProvider(DEFAULT_RECON_PROVIDER);
      }
    };
    loadSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!autoIdentifyPolling) return;
    let cancelled = false;
    const pollStatus = async () => {
      try {
        const status = await getColumnIdentifyStatus(datasetName, tableName);
        if (cancelled) return;
        const resolved = status?.status;
        if (resolved === 'AUTO') {
          setAutoIdentifyPolling(false);
          setAutoDetectStatus('Auto-identification completed.');
          await fetchTableData();
          return;
        }
        if (resolved === 'AUTO_FAILED') {
          setAutoIdentifyPolling(false);
          setAutoDetectStatus('Auto-identification failed. Check your Moose/LLM settings.');
          await fetchTableData();
          return;
        }
      } catch (err) {
        if (!cancelled) {
          setAutoDetectStatus(err?.response?.data?.detail || 'Unable to check Moose job status.');
        }
      }
    };
    pollStatus();
    const timer = setInterval(pollStatus, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [autoIdentifyPolling, datasetName, tableName, fetchTableData]);

  useEffect(() => {
    if (data?.dpv_status === 'DPV_PENDING') {
      if (!dpvIdentifyPolling) {
        setDpvIdentifyPolling(true);
      }
      if (!dpvDetectStatus) {
        setDpvDetectStatus('DPV annotation in progress...');
      }
    }
  }, [data?.dpv_status, dpvIdentifyPolling, dpvDetectStatus]);

  useEffect(() => {
    if (!dpvIdentifyPolling) return;
    let cancelled = false;
    const pollStatus = async () => {
      try {
        const status = await getDpvStatus(datasetName, tableName);
        if (cancelled) return;
        const resolved = status?.status;
        if (resolved === 'DPV') {
          setDpvIdentifyPolling(false);
          setDpvDetectStatus('DPV annotation completed.');
          await fetchTableData();
          return;
        }
        if (resolved === 'DPV_FAILED') {
          setDpvIdentifyPolling(false);
          setDpvDetectStatus('DPV annotation failed. Check your Moose/LLM settings.');
          await fetchTableData();
        }
      } catch (err) {
        if (!cancelled) {
          setDpvDetectStatus(err?.response?.data?.detail || 'Unable to check DPV job status.');
        }
      }
    };
    pollStatus();
    const timer = setInterval(pollStatus, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [dpvIdentifyPolling, datasetName, tableName, fetchTableData]);

  const hasExistingClassification = useMemo(() => {
    const classifiedColumns = data?.classified_columns || {};
    const neColumns = classifiedColumns?.NE || {};
    const litColumns = classifiedColumns?.LIT || {};
    return Object.keys(neColumns).length > 0 || Object.keys(litColumns).length > 0;
  }, [data?.classified_columns]);

  const neColumnIndexes = useMemo(() => {
    const header = data?.header || [];
    const classifiedColumns = data?.classified_columns || {};
    const neColumns = classifiedColumns?.NE || {};
    return Object.keys(neColumns)
      .map((key) => Number(key))
      .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < header.length)
      .sort((left, right) => left - right);
  }, [data?.header, data?.classified_columns]);

  const neColumnIndexSet = useMemo(() => new Set(neColumnIndexes), [neColumnIndexes]);

  const handlePreviousPage = () => {
    if (prevCursor) {
      fetchTableData({ prevCursor });
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (nextCursor) {
      fetchTableData({ nextCursor });
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleSearch = (text) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  const handleSortChange = (params) => {
    setSortParams(params);
    setCurrentPage(1);
  };

  const handleApplyFilter = (filterData) => {
    setActiveFilters(prev => ({
      ...prev,
      includeNeTypes: filterData.includeTypes || [],
      excludeNeTypes: filterData.excludeTypes || [],
      includeTypes: [],
      excludeTypes: []
    }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setActiveFilters({
      includeTypes: [],
      excludeTypes: [],
      includeNeTypes: [],
      excludeNeTypes: []
    });
    setSortParams(DEFAULT_SORT_PARAMS);
    setCurrentPage(1);
  };

  const handleOpenExportDialog = () => {
    setExportDialogOpen(true);
  };

  const handleToggleExportField = (fieldKey) => {
    setExportEnrichmentFields((prev) => {
      if (prev.includes(fieldKey)) {
        return prev.filter((entry) => entry !== fieldKey);
      }
      return [...prev, fieldKey];
    });
  };

  const handleExport = async () => {
    try {
      setExportSubmitting(true);
      const includeReconciliation = exportIncludeReconciliation;
      const selectedFields = includeReconciliation ? exportEnrichmentFields : [];
      const res = await exportTableCsv(datasetName, tableName, {
        includeReconciliation,
        enrichmentFields: selectedFields
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const filenameSuffix = includeReconciliation && selectedFields.length > 0 ? '_export_enriched.csv' : '_export.csv';
      link.setAttribute('download', `${datasetName}_${tableName}${filenameSuffix}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setExportDialogOpen(false);
    } catch (err) {
      setError('Export failed: ' + (err.message || 'Unknown error'));
    } finally {
      setExportSubmitting(false);
    }
  };

  const handleOpenColumnEditor = () => {
    setColumnEditorError(null);
    setColumnEditorOpen(true);
  };

  const handleSaveColumnEditor = async () => {
    try {
      setColumnEditorError(null);
      const payload = buildColumnClassificationPayload(columnClassification);
      await updateColumnClassification(datasetName, tableName, payload);
      setColumnEditorOpen(false);
      await fetchTableData();
    } catch (err) {
      setColumnEditorError(err?.response?.data?.detail || err?.message || 'Failed to update column types.');
    }
  };

  const handleAutoIdentifyColumns = () => {
    setAutoIdentifyOpen(true);
  };

  const handleOpenDpvAnnotation = () => {
    setDpvIdentifyOpen(true);
  };

  const persistLlmSettings = async () => {
    const llmProvider = autoIdentifyConfig.provider.trim();
    const llmModel = autoIdentifyConfig.model.trim();
    if (llmOptions.providers.length > 0 && llmProvider && !llmOptions.providers.includes(llmProvider)) {
      throw new Error('Selected LLM provider is not supported.');
    }
    await updateLlmSettings({
      provider: llmProvider || null,
      model: llmModel || null,
      api_key: showLlmApiKeyInput ? (llmApiKey.trim() || undefined) : undefined
    });
    localStorage.setItem('koala.llmProvider', llmProvider);
    localStorage.setItem('koala.llmModel', llmModel);
    if (showLlmApiKeyInput && llmApiKey.trim()) {
      setLlmHasApiKey(true);
      setLlmApiKey('');
      setShowLlmApiKeyInput(false);
    }
    return { llmProvider, llmModel };
  };

  const handleSubmitAutoIdentify = async (force = false) => {
    try {
      setAutoIdentifySubmitting(true);
      await persistLlmSettings();
      const response = await requestColumnIdentification(datasetName, tableName, { force });
      const modelLabel = response?.llm_provider && response?.llm_model
        ? ` using ${response.llm_provider}:${response.llm_model}`
        : '';
      setAutoDetectStatus(
        response?.job_id
          ? `Auto-identification queued${modelLabel} (job ${response.job_id}).`
          : (response?.detail || `Auto-identification queued${modelLabel}.`)
      );
      setAutoIdentifyPolling(true);
      setAutoIdentifyOpen(false);
      await fetchTableData();
    } catch (err) {
      if (err?.response?.status === 409 && !force) {
        setAutoIdentifyOverwriteConfirmOpen(true);
        return;
      }
      setAutoDetectStatus(err?.response?.data?.detail || err?.message || 'Failed to request auto identification.');
    } finally {
      setAutoIdentifySubmitting(false);
    }
  };

  const handleConfirmAutoIdentify = async () => {
    if (hasExistingClassification) {
      setAutoIdentifyOverwriteConfirmOpen(true);
      return;
    }
    await handleSubmitAutoIdentify(false);
  };

  const handleConfirmOverwriteAutoIdentify = async () => {
    setAutoIdentifyOverwriteConfirmOpen(false);
    await handleSubmitAutoIdentify(true);
  };

  const handleSubmitDpvAnnotation = async () => {
    try {
      setDpvIdentifySubmitting(true);
      await persistLlmSettings();
      const response = await requestDpvAnnotation(datasetName, tableName);
      const modelLabel = response?.llm_provider && response?.llm_model
        ? ` using ${response.llm_provider}:${response.llm_model}`
        : '';
      setDpvDetectStatus(
        response?.job_id
          ? `DPV annotation queued${modelLabel} (job ${response.job_id}).`
          : (response?.detail || `DPV annotation queued${modelLabel}.`)
      );
      setDpvIdentifyPolling(true);
      setDpvIdentifyOpen(false);
      await fetchTableData();
    } catch (err) {
      setDpvDetectStatus(err?.response?.data?.detail || err?.message || 'Failed to request DPV annotation.');
    } finally {
      setDpvIdentifySubmitting(false);
    }
  };

  const toggleRowSelection = (rowId) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const toggleCellSelection = (rowId, colIndex) => {
    const neColumns = data?.classified_columns?.NE || {};
    const isNeColumn = Object.prototype.hasOwnProperty.call(neColumns, colIndex) ||
      Object.prototype.hasOwnProperty.call(neColumns, String(colIndex));
    if (!isNeColumn) return;
    const key = `${rowId}:${colIndex}`;
    setSelectedCells(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleAllRows = (checked) => {
    if (!data?.rows) return;
    setSelectedRows(new Set(checked ? data.rows.map(row => row.idRow) : []));
  };

  const buildReconcilePayload = () => {
    const selectedNeColumns = (reconcileColumns || [])
      .map((idx) => Number(idx))
      .filter((idx) => Number.isInteger(idx) && neColumnIndexSet.has(idx));
    const payload = {
      provider: reconcileProvider,
      scope: reconcileScope,
      top_k: reconcileTopK || undefined
    };

    if (reconcileScope === 'cell') {
      const cells = Array.from(selectedCells).map((entry) => {
        const [row, col] = entry.split(':').map(Number);
        return { row, col };
      }).filter((entry) => neColumnIndexSet.has(entry.col));
      payload.cells = cells;
    } else if (reconcileScope === 'rows') {
      payload.rows = Array.from(selectedRows);
      payload.columns = selectedNeColumns;
    } else if (reconcileScope === 'page') {
      payload.rows = (data?.rows || []).map((row) => row.idRow);
      payload.columns = selectedNeColumns;
    } else if (reconcileScope === 'table') {
      payload.columns = selectedNeColumns;
    }
    return payload;
  };

  const validateReconcileRequest = () => {
    if (neColumnIndexes.length === 0) {
      setReconcileStatus('No NE columns available. Set column types before running reconciliation.');
      return false;
    }
    const providerConfig = reconcileSettings.providers?.[reconcileProvider] || null;
    const providerLabel = getProviderLabel(reconcileProvider, providerConfig);
    const missingItems = Array.isArray(providerConfig?.missing)
      ? providerConfig.missing.filter(Boolean)
      : [];
    if (missingItems.length === 0 && providerConfig) {
      if (providerConfig.requiresApiKey && !providerConfig.hasApiKey) {
        missingItems.push(`${providerLabel} API key`);
      }
      if (providerConfig.usesSharedLlm && !reconcileSettings.llm.isConfigured) {
        missingItems.push('LLM provider/model/API key');
      }
      if (reconcileProvider === 'lion_linker' && !providerConfig.hasLamapiToken) {
        missingItems.push('Lamapi token');
      }
    }
    const uniqueMissing = [...new Set(missingItems)];
    if (uniqueMissing.length > 0) {
      setReconcileStatus(`Missing ${uniqueMissing.join(', ')}. Update your profile first.`);
      return false;
    }
    if (reconcileScope === 'cell' && selectedCells.size === 0) {
      setReconcileStatus('Select at least one cell to reconcile.');
      return false;
    }
    if (reconcileScope === 'cell') {
      const selectedNeCells = Array.from(selectedCells).filter((entry) => {
        const parts = String(entry).split(':');
        if (parts.length !== 2) return false;
        const colIdx = Number(parts[1]);
        return Number.isInteger(colIdx) && neColumnIndexSet.has(colIdx);
      });
      if (selectedNeCells.length === 0) {
        setReconcileStatus('Select at least one NE cell to reconcile.');
        return false;
      }
    }
    if (reconcileScope === 'rows' && selectedRows.size === 0) {
      setReconcileStatus('Select at least one row to reconcile.');
      return false;
    }
    if (reconcileScope !== 'cell') {
      const selectedNeColumns = (reconcileColumns || [])
        .map((idx) => Number(idx))
        .filter((idx) => Number.isInteger(idx) && neColumnIndexSet.has(idx));
      if (selectedNeColumns.length === 0) {
        setReconcileStatus('Select at least one NE column to reconcile.');
        return false;
      }
    }
    return true;
  };

  const startReconcileJob = async () => {
    setReconcileSubmitting(true);
    try {
      const payload = buildReconcilePayload();
      const response = await createReconciliationJob(datasetName, tableName, payload);
      setReconcileJobId(response?.job_id || null);
      setReconcileStatus(
        formatReconcileRuntimeStatus(response?.status || 'queued', response?.progress, reconcileProvider)
      );
      setReconcilePolling(true);
    } catch (err) {
      setReconcileStatus(err?.response?.data?.detail || err?.message || 'Failed to start reconciliation.');
    } finally {
      setReconcileSubmitting(false);
    }
  };

  const handleReconcile = async () => {
    setReconcileStatus(null);
    if (!validateReconcileRequest()) {
      return;
    }
    const scoreRange = data?.reconciliation?.score_range || {};
    const hasExistingReconciliation = scoreRange.min !== null || scoreRange.max !== null;
    if (hasExistingReconciliation) {
      setRerunConfirmOpen(true);
      return;
    }
    await startReconcileJob();
  };

  const handleConfirmRerun = async () => {
    setRerunConfirmOpen(false);
    await startReconcileJob();
  };

  const handleTriggerColumnTypeRanking = async () => {
    try {
      setReconcileColumnTypesTriggering(true);
      const response = await triggerReconciliationColumnTypes(datasetName, tableName, {
        provider: null,
        sample_strategy: reconcileTypeSampleStrategy,
        sample_size: reconcileTypeSampleSize,
        max_types: 10
      });
      setReconcileColumnTypesStatus(response?.status || 'PENDING');
      setReconcileColumnTypesJobId(response?.job_id || null);
      setReconcileColumnTypesConfig(response?.config || null);
      setReconcileColumnTypesError(null);
    } catch (err) {
      setReconcileColumnTypesError(
        err?.response?.data?.detail || err?.message || 'Unable to start NE column type ranking job.'
      );
    } finally {
      setReconcileColumnTypesTriggering(false);
    }
  };

  useEffect(() => {
    const activeJob = data?.reconciliation?.active_job;
    if (!activeJob) return;
    const activeJobId = Number(activeJob?.job_id);
    if (!Number.isInteger(activeJobId) || activeJobId <= 0) return;
    const status = String(activeJob?.status || 'queued').toLowerCase();
    const terminalStatuses = ['completed', 'succeeded', 'success', 'done', 'finished', 'failed', 'error', 'canceled', 'cancelled', 'sync_failed', 'timeout'];
    if (terminalStatuses.includes(status)) return;

    if (activeJob?.provider && activeJob.provider !== reconcileProvider) {
      setReconcileProvider(activeJob.provider);
    }
    if (reconcileJobId !== activeJobId) {
      setReconcileJobId(activeJobId);
    }
    if (!reconcilePolling) {
      setReconcilePolling(true);
      setReconcileStatus(
        formatReconcileRuntimeStatus(status, activeJob?.progress, activeJob?.provider || reconcileProvider)
      );
    }
  }, [data?.reconciliation?.active_job, reconcileJobId, reconcilePolling, reconcileProvider]);

  useEffect(() => {
    if (!reconcilePolling || !reconcileJobId) return;
    let cancelled = false;
    const successStatuses = ['completed', 'succeeded', 'success', 'done', 'finished'];
    const failureStatuses = ['failed', 'error', 'canceled', 'cancelled', 'sync_failed', 'timeout'];
    const pollStatus = async () => {
      try {
        const status = await getReconciliationStatus(datasetName, tableName, reconcileJobId);
        if (cancelled) return;
        const resolved = String(status?.status || 'queued').toLowerCase();
        if (successStatuses.includes(resolved) && status?.synced) {
          setReconcilePolling(false);
          setReconcileStatus('Reconciliation completed.');
          setReconcileJobId(null);
          await fetchTableData();
          await fetchReconciliationColumnTypes();
          return;
        }
        if (failureStatuses.includes(resolved)) {
          setReconcilePolling(false);
          const detail = status?.error?.detail || status?.error || 'Reconciliation failed.';
          setReconcileStatus(detail);
          setReconcileJobId(null);
          return;
        }
        setReconcileStatus(
          formatReconcileRuntimeStatus(resolved, status?.progress, status?.provider || reconcileProvider)
        );
      } catch (err) {
        if (!cancelled) {
          setReconcileStatus(err?.response?.data?.detail || 'Unable to check reconciliation status.');
        }
      }
    };
    pollStatus();
    const timer = setInterval(pollStatus, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [reconcilePolling, reconcileJobId, datasetName, tableName, reconcileProvider, fetchTableData, fetchReconciliationColumnTypes]);

  useEffect(() => {
    if (!['PENDING', 'RUNNING'].includes(reconcileColumnTypesStatus)) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      await fetchReconciliationColumnTypes();
    };
    const timer = setInterval(poll, 3000);
    poll();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [reconcileColumnTypesStatus, fetchReconciliationColumnTypes]);

  const availableTypes = useMemo(() => {
    const raw = data?.reconciliation?.type_summary || [];
    const seen = new Set();
    return raw
      .map((entry) => {
        const id = String(entry?.id ?? '').trim();
        const name = String(entry?.name ?? id).trim();
        return {
          ...entry,
          id,
          name
        };
      })
      .filter((entry) => {
        if (!entry.id) return false;
        const lowered = entry.id.toLowerCase();
        if (['none', 'null', 'undefined'].includes(lowered)) return false;
        if (seen.has(lowered)) return false;
        seen.add(lowered);
        return true;
      });
  }, [data]);

  const reconciliationCells = useMemo(() => {
    return data?.reconciliation?.cells || {};
  }, [data?.reconciliation]);

  const reconciliationScoreRange = useMemo(() => {
    return data?.reconciliation?.score_range || { min: null, max: null };
  }, [data?.reconciliation]);

  const columnDpvAnnotations = useMemo(() => {
    const annotations = data?.dpv_annotations || {};
    return (data?.header || []).map((_, idx) => {
      const entry = annotations[idx] || annotations[String(idx)];
      if (!entry) return null;
      const typeId = entry.type_id || entry.typeId || entry.type || '';
      if (!typeId) return null;
      return {
        typeId,
        confidence: entry.confidence
      };
    });
  }, [data?.dpv_annotations, data?.header]);

  const hasDpvAnnotations = columnDpvAnnotations.some(Boolean);

  const reconciliationColumnTypeColumns = useMemo(() => {
    return reconcileColumnTypes?.columns || {};
  }, [reconcileColumnTypes]);

  const reconciliationColumnTypeTopByIndex = useMemo(() => {
    const map = {};
    Object.entries(reconciliationColumnTypeColumns).forEach(([idx, payload]) => {
      if (payload?.top_type) {
        map[Number(idx)] = payload.top_type;
      }
    });
    return map;
  }, [reconciliationColumnTypeColumns]);

  const selectedNeColumnSummary = useMemo(() => {
    if (selectedNeColumn === null || selectedNeColumn === undefined) return null;
    return reconciliationColumnTypeColumns?.[selectedNeColumn] ||
      reconciliationColumnTypeColumns?.[String(selectedNeColumn)] ||
      null;
  }, [selectedNeColumn, reconciliationColumnTypeColumns]);

  const getReconciliationEntry = (rowId, colIndex) => {
    const rowEntry = reconciliationCells?.[rowId] || reconciliationCells?.[String(rowId)];
    if (!rowEntry) return null;
    return rowEntry[colIndex] || rowEntry[String(colIndex)] || null;
  };

  const openCandidates = async (event, rowId, colIndex, cellValue, providerOverride) => {
    event.stopPropagation();
    setCandidateDialogOpen(true);
    setCandidateLoading(true);
    setCandidateError(null);
    setCandidatePayload(null);
    setCandidateSaveError(null);
    setCandidateSelection(null);
    const provider = providerOverride || reconcileProvider;
    setCandidateCellMeta({
      rowId,
      colIndex,
      value: cellValue,
      provider
    });
    try {
      const response = await getReconciliationCandidates(datasetName, tableName, rowId, colIndex, provider);
      setCandidatePayload(response?.payload || null);
      const current = getReconciliationEntry(rowId, colIndex)?.final;
      if (current?.id || current?.name) {
        setCandidateSelection({
          id: current.id || null,
          name: current.name || null
        });
      }
    } catch (err) {
      setCandidateError(err?.response?.data?.detail || err?.message || 'Failed to load candidates.');
    } finally {
      setCandidateLoading(false);
    }
  };

  const handleCandidateSelect = (candidate) => {
    setCandidateSelection({
      id: candidate?.id || candidate?.entity_id || null,
      name: candidate?.name || candidate?.label || null,
      candidate
    });
  };

  const handleCandidateSave = async () => {
    if (!candidateCellMeta) return;
    if (!candidateSelection?.candidate) {
      setCandidateSaveError('Select a candidate first.');
      return;
    }
    setCandidateSaving(true);
    setCandidateSaveError(null);
    const selected = candidateSelection?.candidate;
    const finalPayload = {
      id: selected?.id || selected?.entity_id || null,
      name: selected?.name || selected?.label || null,
      types: selected?.types || selected?.metadata?.types || null,
      description: selected?.description || selected?.metadata?.description || null,
      confidence_label: selected?.confidence_label || null,
      confidence_score: selected?.confidence_score ?? selected?.score ?? null,
      label: selected?.label || null
    };
    try {
      await updateReconciliationCell(datasetName, tableName, {
        row: candidateCellMeta.rowId,
        col: candidateCellMeta.colIndex,
        provider: candidateCellMeta.provider,
        final: finalPayload
      });
      await fetchTableData();
      await fetchReconciliationColumnTypes();
      setCandidateDialogOpen(false);
    } catch (err) {
      setCandidateSaveError(err?.response?.data?.detail || err?.message || 'Failed to save selection.');
    } finally {
      setCandidateSaving(false);
    }
  };

  const handleCandidateClear = async () => {
    if (!candidateCellMeta) return;
    setCandidateSaving(true);
    setCandidateSaveError(null);
    try {
      await updateReconciliationCell(datasetName, tableName, {
        row: candidateCellMeta.rowId,
        col: candidateCellMeta.colIndex,
        provider: candidateCellMeta.provider,
        final: {}
      });
      await fetchTableData();
      await fetchReconciliationColumnTypes();
      setCandidateDialogOpen(false);
    } catch (err) {
      setCandidateSaveError(err?.response?.data?.detail || err?.message || 'Failed to clear selection.');
    } finally {
      setCandidateSaving(false);
    }
  };

  const buildSubtypeOptions = (type, currentValue) => {
    const options = type === 'NE' ? NER_TYPES : type === 'LIT' ? LIT_TYPES : [];
    if (type === 'NE' && currentValue && !options.includes(currentValue)) {
      return [currentValue, ...options];
    }
    return options;
  };

  const renderLlmSettingsFields = () => (
    <>
      {llmSettingsError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {llmSettingsError}
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {llmOptions.providers.length > 0 ? (
            <FormControl fullWidth>
              <InputLabel>LLM provider</InputLabel>
              <Select
                label="LLM provider"
                value={autoIdentifyConfig.provider}
                onChange={(e) => setAutoIdentifyConfig(prev => ({
                  ...prev,
                  provider: e.target.value
                }))}
              >
                <MenuItem value="">
                  Server default
                </MenuItem>
                {llmOptions.providers.map((provider) => (
                  <MenuItem key={provider} value={provider}>{provider}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="LLM provider"
              fullWidth
              value={autoIdentifyConfig.provider}
              placeholder="openrouter or ollama"
              onChange={(e) => setAutoIdentifyConfig(prev => ({
                ...prev,
                provider: e.target.value
              }))}
              helperText="No providers configured on the server."
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="LLM model"
            fullWidth
            value={autoIdentifyConfig.model}
            placeholder="e.g. gpt-4o-mini"
            onChange={(e) => setAutoIdentifyConfig(prev => ({
              ...prev,
              model: e.target.value
            }))}
            helperText="Leave blank to use the server default."
          />
        </Grid>
        <Grid item xs={12}>
          {!showLlmApiKeyInput && llmHasApiKey ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                LLM API key is stored for your profile.
              </Typography>
              <Button size="small" onClick={() => setShowLlmApiKeyInput(true)}>
                Update key
              </Button>
            </Box>
          ) : (
            <TextField
              label="LLM API key"
              type="password"
              fullWidth
              value={llmApiKey}
              placeholder={llmHasApiKey ? 'Stored in profile (leave blank to keep)' : 'Enter API key'}
              onChange={(e) => setLlmApiKey(e.target.value)}
              helperText={llmHasApiKey ? 'Key is stored for your profile.' : 'Key will be stored for your profile.'}
            />
          )}
        </Grid>
      </Grid>
    </>
  );

  const hasActiveFilters = searchText ||
    activeFilters.includeTypes?.length > 0 ||
    activeFilters.excludeTypes?.length > 0 ||
    activeFilters.includeNeTypes?.length > 0 ||
    activeFilters.excludeNeTypes?.length > 0 ||
    (sortParams.sortBy || DEFAULT_SORT_PARAMS.sortBy) !== DEFAULT_SORT_PARAMS.sortBy ||
    (sortParams.sortDirection || DEFAULT_SORT_PARAMS.sortDirection) !== DEFAULT_SORT_PARAMS.sortDirection ||
    (sortParams.sortConfidenceColumn ?? null) !== DEFAULT_SORT_PARAMS.sortConfidenceColumn;

  if (loading && !data) {
    return <CircularProgress />;
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={
          <Button color="inherit" size="small" onClick={fetchTableData}>
            Retry
          </Button>
        }
      >
        Error loading table data: {error}
      </Alert>
    );
  }

  if (!data || !Array.isArray(data.rows)) {
    return (
      <Card sx={{ m: 2, textAlign: 'center', p: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No data available for this table
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={fetchTableData}
        >
          Refresh
        </Button>
      </Card>
    );
  }

  const candidateList = candidatePayload?.candidate_ranking || candidatePayload?.candidates || [];
  const hasCandidateMatch = candidateList.some((candidate) => candidate.match === true);
  const hasCandidateMatchSignal = candidateList.some(
    (candidate) => candidate?.match !== undefined && candidate?.match !== null
  );
  const isNilCandidateSet = candidateList.length > 0 && hasCandidateMatchSignal && !hasCandidateMatch;

  const classified = data.classified_columns || { NE: {}, LIT: {} };
  const columnTypes = data.header.map((_, idx) =>
    classified?.NE?.hasOwnProperty(idx) ? 'NE'
      : classified?.LIT?.hasOwnProperty(idx) ? 'LIT'
        : ''
  );
  const columnSubtypes = data.header.map((_, idx) =>
    classified?.NE?.hasOwnProperty(idx)
      ? getCoarseNeSubtype(classified.NE[idx])
      : classified?.LIT?.hasOwnProperty(idx)
        ? getCoarseSubtype(classified.LIT[idx])
        : ''
  );
  const columnSpecificSubtypes = data.header.map((_, idx) =>
    classified?.LIT?.hasOwnProperty(idx)
      ? getSpecificSubtype(classified.LIT[idx])
      : ''
  );
  const columnTypeDetails = data.header.map((_, idx) => {
    if (classified?.NE?.hasOwnProperty(idx)) {
      return getClassificationTypeDetails(classified.NE[idx], 'NE');
    }
    if (classified?.LIT?.hasOwnProperty(idx)) {
      return getClassificationTypeDetails(classified.LIT[idx], 'LIT');
    }
    return null;
  });
  const selectedTypeDetails = selectedTypeDetailsIndex !== null
    ? columnTypeDetails[selectedTypeDetailsIndex]
    : null;
  const selectedTypeHeader = selectedTypeDetailsIndex !== null
    ? (data?.header?.[selectedTypeDetailsIndex] || `Column ${selectedTypeDetailsIndex}`)
    : '';
  const showTypeIdInDetails = Boolean(
    selectedTypeDetails?.typeId &&
    selectedTypeDetails.typeId !== selectedTypeDetails?.coarseTypeId &&
    selectedTypeDetails.typeId !== selectedTypeDetails?.fineTypeId
  );
  const classificationStatus = data?.classification_status || 'UNSET';
  const classificationLabel = classificationStatus === 'AUTO_PENDING'
    ? 'Auto-identifying...'
    : classificationStatus === 'AUTO'
      ? 'Auto (Moose)'
      : classificationStatus === 'AUTO_FAILED'
        ? 'Auto failed'
        : classificationStatus;
  const classificationColor = classificationStatus === 'AUTO'
    ? 'success'
    : classificationStatus === 'AUTO_PENDING'
      ? 'info'
      : classificationStatus === 'AUTO_FAILED'
        ? 'error'
        : classificationStatus === 'MANUAL'
          ? 'primary'
          : 'default';
  const autoDetectSeverity = classificationStatus === 'AUTO_FAILED'
    ? 'error'
    : classificationStatus === 'AUTO'
      ? 'success'
      : 'info';
  const dpvStatus = data?.dpv_status || 'UNSET';
  const dpvLabel = dpvStatus === 'DPV_PENDING'
    ? 'DPV: annotating...'
    : dpvStatus === 'DPV'
      ? 'DPV annotated'
      : dpvStatus === 'DPV_FAILED'
        ? 'DPV failed'
        : '';
  const dpvColor = dpvStatus === 'DPV'
    ? 'success'
    : dpvStatus === 'DPV_PENDING'
      ? 'info'
      : dpvStatus === 'DPV_FAILED'
        ? 'error'
        : 'default';
  const dpvDetectSeverity = dpvStatus === 'DPV_FAILED'
    ? 'error'
    : dpvStatus === 'DPV'
      ? 'success'
      : 'info';
  const reconcileSeverity = reconcilePolling
    ? 'info'
    : reconcileStatus && reconcileStatus.toLowerCase().includes('fail')
      ? 'error'
      : reconcileStatus && reconcileStatus.toLowerCase().includes('missing')
        ? 'error'
        : 'success';
  const columnTypeStatus = reconcileColumnTypesStatus || 'UNSET';
  const columnTypeStatusColor = ['READY'].includes(columnTypeStatus)
    ? 'success'
    : ['FAILED'].includes(columnTypeStatus)
      ? 'error'
      : ['PENDING', 'RUNNING'].includes(columnTypeStatus)
        ? 'info'
        : ['STALE'].includes(columnTypeStatus)
          ? 'warning'
          : 'default';
  const columnTypeSampling = reconcileColumnTypes?.sampling || null;
  const activeReconcileProvider = reconcileSettings.providers?.[reconcileProvider] || null;
  const reconcileProviderLabel = getProviderLabel(reconcileProvider, activeReconcileProvider);
  const providerMissingItems = Array.isArray(activeReconcileProvider?.missing)
    ? activeReconcileProvider.missing.filter(Boolean)
    : [];
  const fallbackMissingItems = [];
  if (providerMissingItems.length === 0 && activeReconcileProvider) {
    if (activeReconcileProvider.requiresApiKey && !activeReconcileProvider.hasApiKey) {
      fallbackMissingItems.push(`${reconcileProviderLabel} API key`);
    }
    if (activeReconcileProvider.usesSharedLlm && !reconcileSettings.llm.isConfigured) {
      fallbackMissingItems.push('LLM provider/model/API key');
    }
    if (reconcileProvider === 'lion_linker' && !activeReconcileProvider.hasLamapiToken) {
      fallbackMissingItems.push('Lamapi token');
    }
  }
  const mergedMissingCredentials = providerMissingItems.length > 0
    ? providerMissingItems
    : fallbackMissingItems;
  const missingReconcileCredentials = mergedMissingCredentials.length > 0;
  const missingReconcileMessage = missingReconcileCredentials
    ? `Missing ${[...new Set(mergedMissingCredentials)].join(', ')}. Update your profile to run reconciliation.`
    : '';

  const showRowSelection = reconcileScope === 'rows';
  const showRowIndex = reconcileScope === 'rows' || reconcileScope === 'cell';
  const selectedRowCount = selectedRows.size;
  const selectedCellCount = selectedCells.size;
  const allRowsSelected = data?.rows?.length > 0 && selectedRowCount === data.rows.length;
  const someRowsSelected = selectedRowCount > 0 && selectedRowCount < (data?.rows?.length || 0);
  const showDpvStatusChip = dpvStatus !== 'UNSET' || hasDpvAnnotations;
  const reconcileSelectionSummary = reconcileScope === 'cell'
    ? `Cell mode: ${selectedCellCount} selected.`
    : reconcileScope === 'rows'
      ? `Row mode: ${selectedRowCount} selected.`
      : reconcileScope === 'page'
        ? `Page mode: ${(data?.rows?.length || 0)} rows in scope.`
        : `Table mode: ${(data?.total_rows || 0)} rows in scope.`;
  const compactActionButtonSx = {
    py: 0,
    px: 1,
    minHeight: 28,
    fontSize: '0.76rem',
    textTransform: 'none'
  };
  const compactPanelButtonSx = {
    py: 0,
    px: 0.95,
    minHeight: 26,
    fontSize: '0.74rem',
    textTransform: 'none'
  };
  const compactIconButtonSx = {
    minWidth: 28,
    width: 28,
    height: 28,
    p: 0
  };

  return (
    <Box sx={{ m: { xs: 1, md: 1.5 } }}>
      <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardHeader
          sx={{
            pt: 1,
            pb: 0.75,
            alignItems: 'center',
            bgcolor: '#fbfcff',
            borderBottom: '1px solid #edf1f7',
            '& .MuiCardHeader-content': {
              overflow: 'hidden'
            },
            '& .MuiCardHeader-action': {
              alignSelf: 'flex-start',
              mt: 0.15,
              mr: 0,
              overflow: 'hidden'
            }
          }}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mr: 0.5 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => navigate('/dataset')}
                  sx={{ fontSize: '0.86rem' }}
                >
                  Datasets
                </Link>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => navigate(`/dataset/${encodeURIComponent(datasetName)}`)}
                  sx={{ fontSize: '0.86rem' }}
                >
                  {datasetName}
                </Link>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {tableName}
                </Typography>
              </Breadcrumbs>
              <Chip
                label={data?.status || 'READY'}
                size="small"
                color="success"
              />
              <Chip
                label={`Column types: ${classificationLabel}`}
                size="small"
                color={classificationColor}
                variant="outlined"
              />
              {showDpvStatusChip && (
                <Chip
                  label={dpvLabel || 'DPV'}
                  size="small"
                  color={dpvColor}
                  variant="outlined"
                />
              )}
              {data?.score_column_name && (
                <Chip
                  label={`Score: ${data.score_column_name}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Box>
          }
          action={
            <Box
              sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                alignItems: 'center',
                gap: 0.4,
                justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                maxWidth: { xs: 300, lg: '100%' },
                '& .MuiButton-root': {
                  width: 'auto',
                  flex: '0 0 auto'
                }
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<BuildIcon fontSize="small" />}
                onClick={handleOpenColumnEditor}
                sx={compactActionButtonSx}
              >
                Edit columns
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoFixHighIcon fontSize="small" />}
                onClick={handleAutoIdentifyColumns}
                disabled={loading || classificationStatus === 'AUTO_PENDING'}
                sx={compactActionButtonSx}
              >
                {classificationStatus === 'AUTO_PENDING' ? 'Auto-identifying...' : 'Auto identify'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PolicyIcon fontSize="small" />}
                onClick={handleOpenDpvAnnotation}
                disabled={loading || dpvStatus === 'DPV_PENDING'}
                sx={compactActionButtonSx}
              >
                {dpvStatus === 'DPV_PENDING' ? 'Annotating DPV...' : 'Annotate DPV'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleOpenExportDialog}
                sx={compactActionButtonSx}
              >
                Export CSV
              </Button>
            </Box>
          }
        />

        {autoDetectStatus && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Alert severity={autoDetectSeverity} onClose={() => setAutoDetectStatus(null)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {classificationStatus === 'AUTO_PENDING' && (
                  <CircularProgress size={16} />
                )}
                <span>{autoDetectStatus}</span>
              </Box>
            </Alert>
          </Box>
        )}

        {dpvDetectStatus && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Alert severity={dpvDetectSeverity} onClose={() => setDpvDetectStatus(null)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {dpvStatus === 'DPV_PENDING' && (
                  <CircularProgress size={16} />
                )}
                <span>{dpvDetectStatus}</span>
              </Box>
            </Alert>
          </Box>
        )}

        {reconcileStatus && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Alert severity={reconcileSeverity} onClose={() => setReconcileStatus(null)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {reconcilePolling && (
                  <CircularProgress size={16} />
                )}
                <span>{reconcileStatus}</span>
              </Box>
            </Alert>
          </Box>
        )}

        <CardContent sx={{ px: 1.5, py: 0.75, bgcolor: '#fcfdff' }}>
          <Accordion
            disableGutters
            elevation={0}
            expanded={reconcilePanelExpanded}
            onChange={(_, expanded) => setReconcilePanelExpanded(expanded)}
            sx={{
              border: '1px solid #dde6f3',
              borderRadius: '8px',
              bgcolor: '#f9fbff',
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 34, px: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`Linking · ${reconcileProviderLabel}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Scope: ${reconcileScope}`}
                />
                <Typography variant="caption" color="text.secondary">
                  {reconcileSelectionSummary}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Chip size="small" color={columnTypeStatusColor} label={`NE rank: ${columnTypeStatus}`} />
                <Tooltip title="Run reconciliation" arrow>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleReconcile();
                      }}
                      onFocus={(event) => event.stopPropagation()}
                      disabled={reconcileSubmitting || loading || neColumnIndexes.length === 0}
                      sx={compactIconButtonSx}
                    >
                      {reconcileSubmitting ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon fontSize="small" />}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1, py: 0.75 }}>
              {neColumnIndexes.length === 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  No NE columns found. Set column types before running reconciliation.
                </Alert>
              )}
              {missingReconcileCredentials && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {missingReconcileMessage}
                </Alert>
              )}

              <Grid container spacing={0.75}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Provider</InputLabel>
                    <Select
                      label="Provider"
                      value={reconcileProvider}
                      onChange={(e) => setReconcileProvider(e.target.value)}
                    >
                      {(reconcileSettings.availableProviders || [DEFAULT_RECON_PROVIDER]).map((provider) => (
                        <MenuItem key={provider} value={provider}>
                          {getProviderLabel(provider, reconcileSettings.providers?.[provider])}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Scope</InputLabel>
                    <Select
                      label="Scope"
                      value={reconcileScope}
                      onChange={(e) => setReconcileScope(e.target.value)}
                    >
                      <MenuItem value="cell">Selected cells</MenuItem>
                      <MenuItem value="rows">Selected rows</MenuItem>
                      <MenuItem value="page">Current page</MenuItem>
                      <MenuItem value="table">Whole table</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" disabled={reconcileScope === 'cell' || neColumnIndexes.length === 0}>
                    <InputLabel>NE Columns</InputLabel>
                    <Select
                      label="NE Columns"
                      multiple
                      value={reconcileColumns}
                      onChange={(event) => {
                        const value = event.target.value;
                        const parsed = (Array.isArray(value) ? value : [value])
                          .map((entry) => Number(entry))
                          .filter((idx) => Number.isInteger(idx) && neColumnIndexSet.has(idx));
                        setReconcileColumns(parsed);
                      }}
                      renderValue={(selected) => {
                        if (!selected?.length) return 'No columns';
                        if (selected.length === neColumnIndexes.length) return 'All NE columns';
                        return `${selected.length} columns`;
                      }}
                    >
                      {neColumnIndexes.map((idx) => {
                        const header = data?.header?.[idx] || `Column ${idx}`;
                        return (
                        <MenuItem key={`${header}-${idx}`} value={idx}>
                          <Checkbox checked={reconcileColumns.includes(idx)} />
                          <Typography variant="body2">{header} ({idx})</Typography>
                        </MenuItem>
                      );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Top K"
                    type="number"
                    size="small"
                    fullWidth
                    value={reconcileTopK}
                    inputProps={{ min: 1, max: 100 }}
                    onChange={(e) => setReconcileTopK(Number(e.target.value) || 1)}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <FormHelperText sx={{ m: 0 }}>
                  {reconcileScope === 'cell'
                    ? 'Columns are derived from selected NE cells.'
                    : 'Link columns must be NE columns; Koala maps row/column indexes back automatically.'}
                </FormHelperText>
                {(reconcileScope === 'cell' || reconcileScope === 'rows') && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedRows(new Set());
                      setSelectedCells(new Set());
                    }}
                    sx={compactPanelButtonSx}
                  >
                    Clear selection
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 0.75 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">NE type ranking</Typography>
                {columnTypeSampling?.sampled_cells !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    {columnTypeSampling.sampled_cells}/{columnTypeSampling.total_cells} cells ({columnTypeSampling.strategy})
                  </Typography>
                )}
                {!columnTypeSampling?.sampled_cells && reconcileColumnTypesConfig?.sample_strategy && (
                  <Typography variant="caption" color="text.secondary">
                    {reconcileColumnTypesConfig.sample_strategy} sampling
                  </Typography>
                )}
                {reconcileColumnTypesJobId && (
                  <Typography variant="caption" color="text.secondary">
                    job {reconcileColumnTypesJobId.slice(0, 8)}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Sampling</InputLabel>
                  <Select
                    label="Sampling"
                    value={reconcileTypeSampleStrategy}
                    onChange={(event) => setReconcileTypeSampleStrategy(event.target.value)}
                    disabled={reconcileColumnTypesTriggering || ['PENDING', 'RUNNING'].includes(columnTypeStatus)}
                  >
                    <MenuItem value="auto">Auto</MenuItem>
                    <MenuItem value="latest">Latest</MenuItem>
                    <MenuItem value="random">Random</MenuItem>
                    <MenuItem value="all">All</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  label="Sample"
                  value={reconcileTypeSampleSize}
                  inputProps={{ min: 1, max: 50000 }}
                  onChange={(event) => setReconcileTypeSampleSize(Math.max(1, Number(event.target.value) || 1))}
                  sx={{ width: 96 }}
                  disabled={reconcileTypeSampleStrategy === 'all' || reconcileColumnTypesTriggering || ['PENDING', 'RUNNING'].includes(columnTypeStatus)}
                />
                <Tooltip title="Compute NE type ranking" arrow>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleTriggerColumnTypeRanking}
                      disabled={reconcileColumnTypesTriggering || ['PENDING', 'RUNNING'].includes(columnTypeStatus)}
                      sx={compactIconButtonSx}
                    >
                      {reconcileColumnTypesTriggering || ['PENDING', 'RUNNING'].includes(columnTypeStatus)
                        ? <CircularProgress size={14} />
                        : <AutoGraphIcon fontSize="small" />}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              {reconcileColumnTypesError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {reconcileColumnTypesError}
                </Alert>
              )}
              {!reconcileColumnTypesError && columnTypeStatus === 'UNSET' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  No ranking computed yet. Click any NE header after running compute.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </CardContent>

        <Divider />

        <CardContent sx={{ px: 1.5, py: 1, bgcolor: '#f8fafd' }}>
          <Paper
            variant="outlined"
            sx={{
              p: 0.5,
              borderColor: '#dce6f5',
              bgcolor: '#ffffff'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'wrap'
              }}
            >
              <Box sx={{ flex: '1 1 300px', minWidth: 260 }}>
                <TableSearch
                  onSearch={handleSearch}
                  loading={loading}
                  initialSearchText={searchText}
                  compact
                  noPaper
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  flex: '1 1 420px',
                  justifyContent: 'flex-end',
                  '& .MuiButton-root': {
                    minHeight: 28,
                    fontSize: '0.74rem',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    width: 'auto'
                  }
                }}
              >
                <TableSortControls
                  headers={data?.header || []}
                  columnTypes={columnTypes}
                  hasReconciliationScores={reconciliationScoreRange?.max !== null && reconciliationScoreRange?.max !== undefined}
                  onSort={handleSortChange}
                  currentSortParams={sortParams}
                  hasActiveFilters={hasActiveFilters}
                  onClearFilters={handleClearFilters}
                  compact
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setTypeFilterOpen(true)}
                  disabled={availableTypes.length === 0}
                  sx={compactPanelButtonSx}
                >
                  Filter NE types
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowDpvAnnotations(prev => !prev)}
                  disabled={!hasDpvAnnotations}
                  sx={compactPanelButtonSx}
                >
                  {showDpvAnnotations ? 'Hide DPV' : 'Show DPV'}
                </Button>
                <Button
                  variant={compactTable ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setCompactTable(prev => !prev)}
                  sx={compactPanelButtonSx}
                >
                  {compactTable ? 'Compact: on' : 'Compact: off'}
                </Button>
                {availableTypes.length === 0 && (
                  <Tooltip title="No linked NE types available for filtering.">
                    <Typography variant="caption" color="text.secondary">
                      No linked types yet
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Paper>
        </CardContent>

        <Divider />

        <CardContent sx={{ p: 0 }}>
          <Dialog
            open={selectedNeColumn !== null}
            onClose={() => setSelectedNeColumn(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              NE Column Ranking
            </DialogTitle>
            <DialogContent dividers>
              {selectedNeColumn !== null && (
                <Box>
                  <Typography variant="subtitle2">
                    Col {selectedNeColumn}: {data?.header?.[selectedNeColumn] || 'Unknown'}
                  </Typography>
                  {!selectedNeColumnSummary ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No computed evidence for this column. Run "Compute ranking" first.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Evidence cells: {selectedNeColumnSummary.evidence_cells}
                      </Typography>
                      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(selectedNeColumnSummary.ranking || []).slice(0, 10).map((entry, idx) => (
                          <Paper
                            key={`ne-rank-${selectedNeColumn}-${entry.id || entry.name || idx}`}
                            variant="outlined"
                            sx={{ p: 1, bgcolor: idx === 0 ? '#eef5ff' : 'transparent', borderColor: idx === 0 ? '#9fc0eb' : undefined }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: idx === 0 ? 700 : 500 }}>
                                #{idx + 1} {entry.name || entry.id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {entry.frequency !== undefined
                                  ? `${Math.round(entry.frequency * 100)}% freq`
                                  : entry.probability !== undefined
                                    ? `${Math.round(entry.probability * 100)}%`
                                    : ''}
                              </Typography>
                            </Box>
                            {entry.probability !== undefined && (
                              <Typography variant="caption" color="text.secondary">
                                Weighted share: {Math.round(entry.probability * 100)}%
                              </Typography>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedNeColumn(null)}>Close</Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={candidateDialogOpen}
            onClose={() => setCandidateDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Linking candidates</DialogTitle>
            <DialogContent dividers>
              {candidateCellMeta && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Row {candidateCellMeta.rowId}, Column {candidateCellMeta.colIndex}
                  </Typography>
                  <Typography variant="subtitle1">
                    {candidateCellMeta.value ? String(candidateCellMeta.value) : '(empty)'}
                  </Typography>
                </Box>
              )}
              {candidateLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading candidates…</Typography>
                </Box>
              )}
              {candidateError && (
                <Alert severity="error">{candidateError}</Alert>
              )}
              {!candidateLoading && !candidateError && (
                <Box>
                  {(candidatePayload?.explanation ||
                    candidatePayload?.llm_explanation ||
                    candidatePayload?.meta?.explanation) && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {candidatePayload?.explanation ||
                        candidatePayload?.llm_explanation ||
                        candidatePayload?.meta?.explanation}
                    </Alert>
                  )}
                  {isNilCandidateSet && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      NIL: no candidate was marked as a match.
                    </Alert>
                  )}
                  {candidateList.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No candidates returned for this cell.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {candidateList.map((candidate) => {
                        const idValue = candidate.id || candidate.entity_id || '';
                        const nameValue = candidate.name || candidate.label || '';
                        const isWinner = candidate.match === true;
                        const isSelected = candidateSelection?.id && idValue
                          ? candidateSelection.id === idValue
                          : candidateSelection?.name === nameValue && nameValue;
                        const typesValue = Array.isArray(candidate.types)
                          ? candidate.types.map((type) => ({
                            id: type.id || type.entity_id || '',
                            name: type.name || type.label || ''
                          }))
                          : Array.isArray(candidate.metadata?.types)
                            ? candidate.metadata.types.map((type) => ({
                              id: type.id || type.entity_id || '',
                              name: type.name || type.label || ''
                            }))
                            : [];
                        const descriptionValue = candidate.description || candidate.metadata?.description;
                        const scoreValue = candidate.confidence_score ?? candidate.score;
                        return (
                          <Paper
                            key={`${candidate.entity_id || candidate.id || candidate.rank}-${candidate.rank}`}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderColor: isSelected ? '#90caf9' : undefined,
                              bgcolor: isSelected ? '#eef4ff' : 'transparent'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ textDecoration: isWinner ? 'underline' : 'none' }}
                                >
                                  {nameValue || idValue || 'Candidate'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {idValue || 'n/a'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {candidate.rank !== undefined && candidate.rank !== null && (
                                  <Typography variant="caption" color="text.secondary">
                                    Rank {candidate.rank}
                                  </Typography>
                                )}
                                {isWinner && (
                                  <Chip size="small" label="Winner" color="success" />
                                )}
                                <Button
                                  size="small"
                                  variant={isSelected ? 'contained' : 'outlined'}
                                  onClick={() => handleCandidateSelect(candidate)}
                                >
                                  {isSelected ? 'Selected' : 'Select'}
                                </Button>
                              </Box>
                            </Box>
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {candidate.confidence_label && (
                                <Chip size="small" label={`Confidence: ${candidate.confidence_label}`} />
                              )}
                              {scoreValue !== null && scoreValue !== undefined && (
                                <Chip
                                  size="small"
                                  label={`Score: ${scoreValue}`}
                                />
                              )}
                              {candidate.label && (
                                <Chip size="small" label={`Label: ${candidate.label}`} />
                              )}
                              {idValue && (
                                <Chip
                                  size="small"
                                  label="Wikidata"
                                  component={Link}
                                  href={`https://www.wikidata.org/wiki/${idValue}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  clickable
                                />
                              )}
                            </Box>
                            {typesValue.length > 0 && (
                              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {typesValue.map((type) => (
                                  <Chip
                                    key={`${type.id || type.name}`}
                                    size="small"
                                    label={`${type.name || type.id}${type.id ? ` (${type.id})` : ''}`}
                                  />
                                ))}
                              </Box>
                            )}
                            {descriptionValue && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {descriptionValue}
                              </Typography>
                            )}
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {candidateSaveError && (
                <Typography variant="caption" color="error">
                  {candidateSaveError}
                </Typography>
              )}
              <Button onClick={() => setCandidateDialogOpen(false)}>
                Close
              </Button>
              <Button
                onClick={handleCandidateClear}
                disabled={candidateSaving}
              >
                Clear selection
              </Button>
              <Button
                variant="contained"
                onClick={handleCandidateSave}
                disabled={candidateSaving}
              >
                {candidateSaving ? 'Saving...' : 'Save selection'}
              </Button>
            </DialogActions>
          </Dialog>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              maxHeight: '72vh',
              width: '100%',
              overflow: 'auto',
              borderTop: '1px solid #e6ebf2',
              borderRadius: 0,
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px'
              }
            }}
          >
            <Table
              stickyHeader
              size={compactTable ? 'small' : 'medium'}
              sx={{
                minWidth: 650,
                tableLayout: 'auto'
              }}
            >
              <TableHead>
              <TableHeader
                headers={data?.header || []}
                columnTypes={columnTypes}
                columnSubtypes={columnSubtypes}
                columnSpecificSubtypes={columnSpecificSubtypes}
                columnTypeDetails={columnTypeDetails}
                columnDpvAnnotations={columnDpvAnnotations}
                columnReconciliationTypes={reconciliationColumnTypeTopByIndex}
                showDpvAnnotations={showDpvAnnotations}
                onNeColumnClick={(colIndex) => setSelectedNeColumn(colIndex)}
                onOpenColumnTypeDetails={(colIndex) => {
                  setSelectedTypeDetailsIndex(colIndex);
                  setColumnTypeDetailsOpen(true);
                }}
                activeNeColumn={selectedNeColumn}
                showRowSelection={showRowSelection}
                showRowIndex={showRowIndex}
                allRowsSelected={allRowsSelected}
                someRowsSelected={someRowsSelected}
                onToggleAllRows={handleToggleAllRows}
                compact={compactTable}
              />
              </TableHead>
              <TableBody>
                {data?.rows?.length > 0 ? (
                  data.rows.map((row) => (
                    <TableRow
                      key={row.idRow}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        backgroundColor: selectedRows.has(row.idRow) ? '#e3f2fd' : 'inherit',
                        '&:hover': {
                          backgroundColor: selectedRows.has(row.idRow) ? '#dceeff' : '#f1f7fd'
                        },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      {showRowIndex && (
                        <TableCell
                          sx={{
                            minWidth: 72,
                            maxWidth: 104,
                            verticalAlign: 'top',
                            padding: compactTable ? '6px 8px' : '8px 10px',
                            textAlign: 'center',
                            bgcolor: selectedRows.has(row.idRow) ? '#dcedff' : '#f5f7fa',
                            position: 'sticky',
                            left: 0,
                            zIndex: 2
                          }}
                        >
                          {showRowSelection ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <Checkbox
                                  size="small"
                                  checked={selectedRows.has(row.idRow)}
                                  onChange={() => toggleRowSelection(row.idRow)}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {row.idRow}
                                </Typography>
                              </Box>
                              {typeof row.reconciliation_score === 'number' && (
                                <Tooltip title="Row confidence (average across linked NE cells)" arrow>
                                  <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#4b5b6b' }}>
                                    {Math.round(row.reconciliation_score * 100)}%
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                              <Typography variant="caption" color="text.secondary">
                                {row.idRow}
                              </Typography>
                              {typeof row.reconciliation_score === 'number' && (
                                <Tooltip title="Row confidence (average across linked NE cells)" arrow>
                                  <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#4b5b6b' }}>
                                    {Math.round(row.reconciliation_score * 100)}%
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      )}
                      {row.data.map((cell, colIndex) => (
                        (() => {
                          const reconEntry = getReconciliationEntry(row.idRow, colIndex);
                          const reconLabel = reconEntry?.final?.name || reconEntry?.final?.id || '';
                          const hasCandidates = Array.isArray(reconEntry?.candidate_ranking)
                            ? reconEntry.candidate_ranking.length > 0
                            : false;
                          const hasMatch = Array.isArray(reconEntry?.candidate_ranking)
                            ? reconEntry.candidate_ranking.some((candidate) => candidate.match === true)
                            : false;
                          const hasMatchSignal = Array.isArray(reconEntry?.candidate_ranking)
                            ? reconEntry.candidate_ranking.some((candidate) => candidate?.match !== undefined && candidate?.match !== null)
                            : false;
                          const reconConfidence = reconEntry?.final?.confidence_score;
                          const providerId = reconEntry?.provider || reconcileProvider;
                          const providerMeta = reconcileSettings.providers?.[providerId];
                          const providerTag = getProviderTag(providerId, providerMeta);
                          const reconTitle = reconLabel
                            ? `${reconLabel}${typeof reconConfidence === 'number'
                              ? ` (${Math.round(reconConfidence * 100)}%)`
                              : ''}`
                            : '';
                          const isNeColumn = neColumnIndexSet.has(colIndex);
                          const isCellSelected = selectedCells.has(`${row.idRow}:${colIndex}`);
                          const isReconciled = Boolean(reconLabel);
                          const isNil = !reconLabel && hasCandidates && hasMatchSignal && !hasMatch;
                          const showCandidatesChip = !reconLabel && hasCandidates && !isNil;
                          return (
                        <TableCell
                          key={colIndex}
                          sx={{
                            minWidth: 100,
                            maxWidth: 320,
                            verticalAlign: 'top',
                            padding: compactTable ? '6px 10px' : '8px 12px',
                            cursor: reconcileScope === 'cell' && isNeColumn ? 'pointer' : 'default',
                            bgcolor: isCellSelected
                              ? '#e8f0fe'
                              : isReconciled
                                ? '#f7fbff'
                                : 'inherit',
                            borderBottom: isCellSelected ? '2px solid #90caf9' : undefined
                          }}
                          onClick={() => {
                            if (reconcileScope === 'cell' && isNeColumn) {
                              toggleCellSelection(row.idRow, colIndex);
                            }
                          }}
                        >
                          <TruncatedCell content={cell} maxLength={150} />
                          {reconLabel && (
                            <Tooltip title={reconTitle || 'Linked entity'} arrow>
                              <Chip
                                label={`${providerTag}: ${reconLabel}`}
                                size="small"
                                variant="outlined"
                                onClick={(event) => openCandidates(event, row.idRow, colIndex, cell, reconEntry?.provider)}
                                sx={{
                                  mt: 0.5,
                                  fontSize: '0.6rem',
                                  color: '#1a4f8b',
                                  borderColor: '#c4d8f2',
                                  bgcolor: '#eef4ff'
                                }}
                              />
                            </Tooltip>
                          )}
                          {isNil && (
                            <Chip
                              label={`${providerTag}: NIL`}
                              size="small"
                              variant="outlined"
                              onClick={(event) => openCandidates(event, row.idRow, colIndex, cell, reconEntry?.provider)}
                              sx={{
                                mt: 0.5,
                                fontSize: '0.6rem',
                                color: '#9a6700',
                                borderColor: '#f3d19e',
                                bgcolor: '#fff8e1'
                              }}
                            />
                          )}
                          {showCandidatesChip && (
                            <Chip
                              label={`${providerTag}: candidates`}
                              size="small"
                              variant="outlined"
                              onClick={(event) => openCandidates(event, row.idRow, colIndex, cell, reconEntry?.provider)}
                              sx={{
                                mt: 0.5,
                                fontSize: '0.6rem',
                                color: '#4a5568',
                                borderColor: '#d4d8df',
                                bgcolor: '#f5f7fb'
                              }}
                            />
                          )}
                        </TableCell>
                          );
                        })()
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={(data?.header || []).length + (showRowIndex ? 1 : 0)}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      {loading ? (
                        <CircularProgress size={32} />
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" color="text.secondary">
                            No rows found{hasActiveFilters ? ' matching the current filters' : ''}
                          </Typography>
                          {hasActiveFilters && (
                            <Button size="small" onClick={handleClearFilters}>
                              Clear filters
                            </Button>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.25,
            borderTop: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {data?.rows?.length > 0 ? `${data.rows.length} rows displayed` : 'No rows found'}
            {data?.total_matches && ` (${data.total_matches} total matches)`}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              disabled={!prevCursor}
              onClick={handlePreviousPage}
              startIcon={<NavigateBeforeIcon />}
              sx={{ mr: 1 }}
              color="primary"
              variant="outlined"
              size="small"
            >
              Previous
            </Button>

            <Box
              sx={{
                px: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: 'action.selected',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Page {currentPage}
              </Typography>
            </Box>

            <Button
              disabled={!nextCursor}
              onClick={handleNextPage}
              endIcon={<NavigateNextIcon />}
              sx={{ ml: 1 }}
              color="primary"
              variant="outlined"
              size="small"
            >
              Next
            </Button>
          </Box>
        </Box>
      </Card>

      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export CSV</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportIncludeReconciliation}
                    onChange={(event) => setExportIncludeReconciliation(event.target.checked)}
                  />
                }
                label="Include reconciliation enrichment columns"
              />
            </Grid>

            {exportIncludeReconciliation && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Select enrichment attributes
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 0.5
                    }}
                  >
                    {EXPORT_ENRICHMENT_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={exportEnrichmentFields.includes(option.key)}
                            onChange={() => handleToggleExportField(option.key)}
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </Box>
                  <FormHelperText>
                    The selected attributes are appended per NE column, using the latest result per cell across all reconcilers.
                  </FormHelperText>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} color="inherit" disabled={exportSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={exportSubmitting || (exportIncludeReconciliation && exportEnrichmentFields.length === 0)}
          >
            {exportSubmitting ? 'Exporting...' : 'Download CSV'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rerunConfirmOpen}
        onClose={() => setRerunConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" fontSize="small" />
          Re-run reconciliation?
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Existing reconciliation results will be updated with the new run.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Do you want to continue and start a new reconciliation job?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRerunConfirmOpen(false)} color="inherit" disabled={reconcileSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRerun}
            variant="contained"
            color="warning"
            disabled={reconcileSubmitting}
          >
            {reconcileSubmitting ? 'Starting...' : 'Yes, run again'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={autoIdentifyOverwriteConfirmOpen}
        onClose={() => setAutoIdentifyOverwriteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" fontSize="small" />
          Overwrite classification?
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            This table already has a saved NE/LIT classification.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Running auto-identify again will replace existing column types with the new Moose result.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAutoIdentifyOverwriteConfirmOpen(false)}
            color="inherit"
            disabled={autoIdentifySubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmOverwriteAutoIdentify}
            variant="contained"
            color="warning"
            disabled={autoIdentifySubmitting}
          >
            {autoIdentifySubmitting ? 'Starting...' : 'Yes, overwrite'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={columnTypeDetailsOpen}
        onClose={() => setColumnTypeDetailsOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Column type details</DialogTitle>
        <DialogContent dividers>
          {selectedTypeDetails ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Typography variant="body2" color="text.secondary">
                Column {selectedTypeDetailsIndex}: <b>{selectedTypeHeader}</b>
              </Typography>
              <Typography variant="body2">
                Group: <b>{selectedTypeDetails.group || 'N/A'}</b>
              </Typography>
              {selectedTypeDetails.coarseTypeId && (
                <Typography variant="body2">
                  Coarse type: <b>{selectedTypeDetails.coarseTypeId}</b>
                </Typography>
              )}
              {selectedTypeDetails.fineTypeId && (
                <Typography variant="body2">
                  Fine type: <b>{selectedTypeDetails.fineTypeId}</b>
                </Typography>
              )}
              {showTypeIdInDetails && (
                <Typography variant="body2">
                  Type ID: <b>{selectedTypeDetails.typeId}</b>
                </Typography>
              )}
              {selectedTypeDetails.confidence !== null && (
                <Typography variant="body2">
                  Confidence: <b>{Math.round(selectedTypeDetails.confidence * 100)}%</b>
                </Typography>
              )}
              {selectedTypeDetails.fineConfidence !== null && (
                <Typography variant="body2">
                  Fine confidence: <b>{Math.round(selectedTypeDetails.fineConfidence * 100)}%</b>
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No additional type details are available for this column.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnTypeDetailsOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <TypeFilterModal
        open={typeFilterOpen}
        onClose={() => setTypeFilterOpen(false)}
        onApplyFilter={handleApplyFilter}
        columnName="All columns"
        availableTypes={availableTypes || []}
        loading={loading}
      />

      <Dialog open={autoIdentifyOpen} onClose={() => setAutoIdentifyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Auto identify column types</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Moose samples table rows and uses your LLM service to assign NE/LIT subtypes. You can
            tweak the result in the column editor afterward.
          </Typography>
          {renderLlmSettingsFields()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoIdentifyOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAutoIdentify}
            disabled={autoIdentifySubmitting}
          >
            {autoIdentifySubmitting ? 'Starting…' : 'Run auto identify'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dpvIdentifyOpen} onClose={() => setDpvIdentifyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Annotate columns with DPV</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Moose uses your LLM service to map columns to DPV (privacy) types. DPV annotations are
            shown separately from NE/LIT column types.
          </Typography>
          {renderLlmSettingsFields()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDpvIdentifyOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitDpvAnnotation}
            disabled={dpvIdentifySubmitting}
          >
            {dpvIdentifySubmitting ? 'Starting...' : 'Run DPV annotation'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={columnEditorOpen} onClose={() => setColumnEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Column Types</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update the NE/LIT classification for each column. Semantic (KG) types are assigned separately to NE columns.
          </Typography>
          <Grid container spacing={2}>
            {(data?.header || []).map((header, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #eee',
                    borderRadius: 1,
                    p: 1,
                    bgcolor: '#fafafa'
                  }}
                >
                  <Chip label={`Col ${idx}: ${header}`} sx={{ mr: 2 }} color="primary" />
                  <FormControl size="small" sx={{ minWidth: 110, mr: 2 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={columnClassification[idx]?.type || 'IGNORED'}
                      label="Type"
                      onChange={e => setColumnClassification(prev => ({
                        ...prev,
                        [idx]: {
                          type: e.target.value,
                          subtype: '',
                          rawSubtype: '',
                          derivedSubtype: ''
                        }
                      }))}
                    >
                      {COLUMN_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {columnClassification[idx]?.type === 'NE' && (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Subtype</InputLabel>
                      <Select
                        value={columnClassification[idx]?.subtype || ''}
                        label="Subtype"
                        onChange={e => setColumnClassification(prev => ({
                          ...prev,
                          [idx]: {
                            ...prev[idx],
                            subtype: e.target.value,
                            rawSubtype: e.target.value,
                            derivedSubtype: e.target.value
                          }
                        }))}
                      >
                        {buildSubtypeOptions('NE', columnClassification[idx]?.subtype).map((subtype) => (
                          <MenuItem key={subtype} value={subtype}>{subtype}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {columnClassification[idx]?.type === 'LIT' && (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Subtype</InputLabel>
                      <Select
                        value={columnClassification[idx]?.subtype || ''}
                        label="Subtype"
                        onChange={e => setColumnClassification(prev => ({
                          ...prev,
                          [idx]: {
                            ...prev[idx],
                            subtype: e.target.value,
                            rawSubtype: '',
                            derivedSubtype: e.target.value
                          }
                        }))}
                      >
                        {buildSubtypeOptions('LIT', columnClassification[idx]?.subtype).map((subtype) => (
                          <MenuItem key={subtype} value={subtype}>{subtype}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
          {columnEditorError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {columnEditorError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnEditorOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveColumnEditor}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TableDataViewer;
